import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Calculate the cutoff date based on days to keep
 * @param {number} daysToKeep - Number of days to keep from today
 * @returns {Date} - Cutoff date
 */
const getCutoffDate = (daysToKeep) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
  cutoffDate.setHours(23, 59, 59, 999); // End of the cutoff day
  return cutoffDate;
};

/**
 * Get date range for deletion - fixed 30 days
 * @returns {Object} - { startDate, endDate }
 */
const getDateRange = () => {
  const cutoffDate = getCutoffDate(30); // Fixed 30 days
  return {
    startDate: new Date(0), // Beginning of time
    endDate: cutoffDate
  };
};

/**
 * Delete files from Firebase Storage
 * @param {string} storagePath - Storage path to delete
 */
const deleteStorageFiles = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath);
    const listResult = await listAll(storageRef);
    
    // Delete all files in the path
    const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);

  } catch (error) {
    console.error(`Error deleting storage files from ${storagePath}:`, error);
  }
};

/**
 * Auto-delete old today+tomorrow uploads (30 days)
 * @returns {Object} - Deletion results
 */
export const autoDeleteTodayTomorrow = async () => {
  const results = {
    deletedSubcategories: 0,
    deletedUploads: 0,
    errors: []
  };

  try {
    const dateRange = getDateRange();

    // Get all today+tomorrow subcategories
    const subcategoriesSnapshot = await getDocs(collection(db, 'todayTomorrow'));
    
    for (const subcategoryDoc of subcategoriesSnapshot.docs) {
      const subcategoryData = subcategoryDoc.data();
      const subcategoryId = subcategoryDoc.id;
      let deletedUploadsInSubcategory = 0;
      
      // Check if subcategory is in deletion range
      let shouldDeleteSubcategory = false;
      if (subcategoryData.createdAt) {
        const createdDate = subcategoryData.createdAt.toDate ? subcategoryData.createdAt.toDate() : new Date(subcategoryData.createdAt);
        shouldDeleteSubcategory = createdDate >= dateRange.startDate && createdDate <= dateRange.endDate;
      }

      // Get uploads in this subcategory
      const uploadsSnapshot = await getDocs(collection(db, 'todayTomorrow', subcategoryId, 'upload'));

      for (const uploadDoc of uploadsSnapshot.docs) {
        const uploadData = uploadDoc.data();
        
        if (uploadData.createdAt) {
          const createdDate = uploadData.createdAt.toDate ? uploadData.createdAt.toDate() : new Date(uploadData.createdAt);
          
          if (createdDate >= dateRange.startDate && createdDate <= dateRange.endDate) {
            try {
              // Delete from storage if storagePath exists
              if (uploadData.storagePath) {
                await deleteStorageFiles(uploadData.storagePath);
              }
              
              // Delete from Firestore
              await deleteDoc(doc(db, 'todayTomorrow', subcategoryId, 'upload', uploadDoc.id));
              deletedUploadsInSubcategory++;
              results.deletedUploads++;

            } catch (error) {
              console.error(`Error deleting today+tomorrow upload ${uploadDoc.id}:`, error);
              results.errors.push(`Upload ${uploadDoc.id}: ${error.message}`);
            }
          }
        }
      }

      // If subcategory should be deleted or has no uploads left, delete the subcategory
      const remainingUploads = await getDocs(collection(db, 'todayTomorrow', subcategoryId, 'upload'));
      if (shouldDeleteSubcategory || remainingUploads.empty) {
        try {
          // Delete any remaining uploads
          for (const uploadDoc of remainingUploads.docs) {
            const uploadData = uploadDoc.data();
            if (uploadData.storagePath) {
              await deleteStorageFiles(uploadData.storagePath);
            }
            await deleteDoc(doc(db, 'todayTomorrow', subcategoryId, 'upload', uploadDoc.id));
          }
          
          // Delete the subcategory document
          await deleteDoc(doc(db, 'todayTomorrow', subcategoryId));
          results.deletedSubcategories++;

        } catch (error) {
          console.error(`Error deleting today+tomorrow subcategory ${subcategoryId}:`, error);
          results.errors.push(`Subcategory ${subcategoryId}: ${error.message}`);
        }
      }
    }
  } catch (error) {
    console.error('Error in autoDeleteTodayTomorrow:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  return results;
};

/**
 * Auto-delete old trending uploads (30 days)
 * @returns {Object} - Deletion results
 */
export const autoDeleteTrending = async () => {
  const results = {
    deletedSubcategories: 0,
    deletedUploads: 0,
    errors: []
  };

  try {
    const dateRange = getDateRange();

    // Get all trending subcategories
    const subcategoriesSnapshot = await getDocs(collection(db, 'trending'));
    
    for (const subcategoryDoc of subcategoriesSnapshot.docs) {
      const subcategoryData = subcategoryDoc.data();
      const subcategoryId = subcategoryDoc.id;
      let deletedUploadsInSubcategory = 0;
      
      // Check if subcategory is in deletion range
      let shouldDeleteSubcategory = false;
      if (subcategoryData.createdAt) {
        const createdDate = subcategoryData.createdAt.toDate ? subcategoryData.createdAt.toDate() : new Date(subcategoryData.createdAt);
        shouldDeleteSubcategory = createdDate >= dateRange.startDate && createdDate <= dateRange.endDate;
      }

      // Get uploads in this subcategory
      const uploadsSnapshot = await getDocs(collection(db, 'trending', subcategoryId, 'upload'));

      for (const uploadDoc of uploadsSnapshot.docs) {
        const uploadData = uploadDoc.data();
        
        if (uploadData.createdAt) {
          const createdDate = uploadData.createdAt.toDate ? uploadData.createdAt.toDate() : new Date(uploadData.createdAt);
          
          if (createdDate >= dateRange.startDate && createdDate <= dateRange.endDate) {
            try {
              // Delete from storage if storagePath exists
              if (uploadData.storagePath) {
                await deleteStorageFiles(uploadData.storagePath);
              }
              
              // Delete from Firestore
              await deleteDoc(doc(db, 'trending', subcategoryId, 'upload', uploadDoc.id));
              deletedUploadsInSubcategory++;
              results.deletedUploads++;

            } catch (error) {
              console.error(`Error deleting trending upload ${uploadDoc.id}:`, error);
              results.errors.push(`Upload ${uploadDoc.id}: ${error.message}`);
            }
          }
        }
      }

      // If subcategory should be deleted or has no uploads left, delete the subcategory
      const remainingUploads = await getDocs(collection(db, 'trending', subcategoryId, 'upload'));
      if (shouldDeleteSubcategory || remainingUploads.empty) {
        try {
          // Delete any remaining uploads
          for (const uploadDoc of remainingUploads.docs) {
            const uploadData = uploadDoc.data();
            if (uploadData.storagePath) {
              await deleteStorageFiles(uploadData.storagePath);
            }
            await deleteDoc(doc(db, 'trending', subcategoryId, 'upload', uploadDoc.id));
          }
          
          // Delete the subcategory document
          await deleteDoc(doc(db, 'trending', subcategoryId));
          results.deletedSubcategories++;

        } catch (error) {
          console.error(`Error deleting trending subcategory ${subcategoryId}:`, error);
          results.errors.push(`Subcategory ${subcategoryId}: ${error.message}`);
        }
      }
    }
  } catch (error) {
    console.error('Error in autoDeleteTrending:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  return results;
};

/**
 * Auto-delete old greetings uploads (30 days)
 * @returns {Object} - Deletion results
 */
export const autoDeleteGreetings = async () => {
  const results = {
    deletedSubcategories: 0,
    deletedUploads: 0,
    errors: []
  };

  try {
    const dateRange = getDateRange();

    // Get all greetings subcategories
    const subcategoriesSnapshot = await getDocs(collection(db, 'greetings'));
    
    for (const subcategoryDoc of subcategoriesSnapshot.docs) {
      const subcategoryData = subcategoryDoc.data();
      const subcategoryId = subcategoryDoc.id;
      let deletedUploadsInSubcategory = 0;
      
      // Check if subcategory is in deletion range
      let shouldDeleteSubcategory = false;
      if (subcategoryData.createdAt) {
        const createdDate = subcategoryData.createdAt.toDate ? subcategoryData.createdAt.toDate() : new Date(subcategoryData.createdAt);
        shouldDeleteSubcategory = createdDate >= dateRange.startDate && createdDate <= dateRange.endDate;
      }

      // Get uploads in this subcategory
      const uploadsSnapshot = await getDocs(collection(db, 'greetings', subcategoryId, 'upload'));

      for (const uploadDoc of uploadsSnapshot.docs) {
        const uploadData = uploadDoc.data();
        
        if (uploadData.createdAt) {
          const createdDate = uploadData.createdAt.toDate ? uploadData.createdAt.toDate() : new Date(uploadData.createdAt);
          
          if (createdDate >= dateRange.startDate && createdDate <= dateRange.endDate) {
            try {
              // Delete from storage if storagePath exists
              if (uploadData.storagePath) {
                await deleteStorageFiles(uploadData.storagePath);
              }
              
              // Delete from Firestore
              await deleteDoc(doc(db, 'greetings', subcategoryId, 'upload', uploadDoc.id));
              deletedUploadsInSubcategory++;
              results.deletedUploads++;

            } catch (error) {
              console.error(`Error deleting greetings upload ${uploadDoc.id}:`, error);
              results.errors.push(`Upload ${uploadDoc.id}: ${error.message}`);
            }
          }
        }
      }

      // If subcategory should be deleted or has no uploads left, delete the subcategory
      const remainingUploads = await getDocs(collection(db, 'greetings', subcategoryId, 'upload'));
      if (shouldDeleteSubcategory || remainingUploads.empty) {
        try {
          // Delete any remaining uploads
          for (const uploadDoc of remainingUploads.docs) {
            const uploadData = uploadDoc.data();
            if (uploadData.storagePath) {
              await deleteStorageFiles(uploadData.storagePath);
            }
            await deleteDoc(doc(db, 'greetings', subcategoryId, 'upload', uploadDoc.id));
          }
          
          // Delete the subcategory document
          await deleteDoc(doc(db, 'greetings', subcategoryId));
          results.deletedSubcategories++;

        } catch (error) {
          console.error(`Error deleting greetings subcategory ${subcategoryId}:`, error);
          results.errors.push(`Subcategory ${subcategoryId}: ${error.message}`);
        }
      }
    }
  } catch (error) {
    console.error('Error in autoDeleteGreetings:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  return results;
};

/**
 * Auto-delete old leader uploads (30 days)
 * @returns {Object} - Deletion results
 */
export const autoDeleteLeaderUploads = async () => {
  const results = {
    deletedSubcategories: 0,
    deletedUploads: 0,
    errors: []
  };

  try {
    const dateRange = getDateRange();

    // Get all leaders
    const leadersSnapshot = await getDocs(collection(db, 'myleaders'));
    
    for (const leaderDoc of leadersSnapshot.docs) {
      const leaderId = leaderDoc.id;
      
      // Get subcategories for this leader
      const subcategoriesSnapshot = await getDocs(collection(db, 'myleaders', leaderId, 'subcategories'));
      
      for (const subcategoryDoc of subcategoriesSnapshot.docs) {
        const subcategoryData = subcategoryDoc.data();
        const subcategoryId = subcategoryDoc.id;
        let deletedUploadsInSubcategory = 0;
        
        // Check if subcategory is in deletion range
        let shouldDeleteSubcategory = false;
        if (subcategoryData.createdAt) {
          const createdDate = subcategoryData.createdAt.toDate ? subcategoryData.createdAt.toDate() : new Date(subcategoryData.createdAt);
          shouldDeleteSubcategory = createdDate >= dateRange.startDate && createdDate <= dateRange.endDate;
        }

        // Get uploads in this subcategory
        const uploadsSnapshot = await getDocs(collection(db, 'myleaders', leaderId, 'subcategories', subcategoryId, 'upload'));
  
        for (const uploadDoc of uploadsSnapshot.docs) {
          const uploadData = uploadDoc.data();
          
          if (uploadData.createdAt) {
            const createdDate = uploadData.createdAt.toDate ? uploadData.createdAt.toDate() : new Date(uploadData.createdAt);
            
            if (createdDate >= dateRange.startDate && createdDate <= dateRange.endDate) {
              try {
                // Delete from storage if storagePath exists
                if (uploadData.storagePath) {
                  await deleteStorageFiles(uploadData.storagePath);
                }
                
                // Delete from Firestore
                await deleteDoc(doc(db, 'myleaders', leaderId, 'subcategories', subcategoryId, 'upload', uploadDoc.id));
                deletedUploadsInSubcategory++;
                results.deletedUploads++;

              } catch (error) {
                console.error(`Error deleting leader upload ${uploadDoc.id}:`, error);
                results.errors.push(`Upload ${uploadDoc.id}: ${error.message}`);
              }
            }
          }
        }

        // If subcategory should be deleted or has no uploads left, delete the subcategory
        const remainingUploads = await getDocs(collection(db, 'myleaders', leaderId, 'subcategories', subcategoryId, 'upload'));
        if (shouldDeleteSubcategory || remainingUploads.empty) {
          try {
            // Delete any remaining uploads
            for (const uploadDoc of remainingUploads.docs) {
              const uploadData = uploadDoc.data();
              if (uploadData.storagePath) {
                await deleteStorageFiles(uploadData.storagePath);
              }
              await deleteDoc(doc(db, 'myleaders', leaderId, 'subcategories', subcategoryId, 'upload', uploadDoc.id));
            }
            
            // Delete the subcategory document
            await deleteDoc(doc(db, 'myleaders', leaderId, 'subcategories', subcategoryId));
            results.deletedSubcategories++;

          } catch (error) {
            console.error(`Error deleting leader subcategory ${subcategoryId}:`, error);
            results.errors.push(`Subcategory ${subcategoryId}: ${error.message}`);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in autoDeleteLeaderUploads:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  return results;
};

/**
 * Run auto-delete for all collections
 * @param {Object} settings - Auto-delete settings
 * @returns {Object} - Combined deletion results
 */
export const runAutoDelete = async (settings) => {
  const results = {
    todayTomorrow: { deletedSubcategories: 0, deletedUploads: 0, errors: [] },
    trending: { deletedSubcategories: 0, deletedUploads: 0, errors: [] },
    greetings: { deletedSubcategories: 0, deletedUploads: 0, errors: [] },
    leaderUploads: { deletedSubcategories: 0, deletedUploads: 0, errors: [] },
    totalDeleted: 0,
    totalErrors: 0
  };

  try {
    // Run auto-delete for today+tomorrow if enabled
    if (settings.autoDeleteTodayTomorrowEnabled) {
      
      results.todayTomorrow = await autoDeleteTodayTomorrow();
    }

    // Run auto-delete for trending if enabled
    if (settings.autoDeleteTrendingEnabled) {
      
      results.trending = await autoDeleteTrending();
    }

    // Run auto-delete for greetings if enabled
    if (settings.autoDeleteGreetingsEnabled) {
      
      results.greetings = await autoDeleteGreetings();
    }

    // Run auto-delete for leader uploads if enabled
    if (settings.autoDeleteLeaderUploadsEnabled) {
      
      results.leaderUploads = await autoDeleteLeaderUploads();
    }

    // Calculate totals
    results.totalDeleted = 
      results.todayTomorrow.deletedSubcategories + results.todayTomorrow.deletedUploads +
      results.trending.deletedSubcategories + results.trending.deletedUploads +
      results.greetings.deletedSubcategories + results.greetings.deletedUploads +
      results.leaderUploads.deletedSubcategories + results.leaderUploads.deletedUploads;

    results.totalErrors = 
      results.todayTomorrow.errors.length +
      results.trending.errors.length +
      results.greetings.errors.length +
      results.leaderUploads.errors.length;

  } catch (error) {
    console.error('Error in runAutoDelete:', error);
    results.totalErrors++;
  }

  return results;
};
